import { apiClient } from './apiClient';
import {
  DashboardSummary,
  JobsCreatedReport,
  LostJobsReport,
  ConversionFunnel,
  UserComparisonReport,
  LeadSourceReport,
  RevenueByMonthReport,
  RevenueByUserReport,
  CycleTimeReport,
  PresentationOutcomeReport,
  AppointmentCompletionReport,
  SpecBreakdownReport,
  ExtendedReportRequest,
} from '../types/reporting';

class ReportingService {
  private static instance: ReportingService;
  private baseUrl = '/Reports';

  public static getInstance(): ReportingService {
    if (!ReportingService.instance) {
      ReportingService.instance = new ReportingService();
    }
    return ReportingService.instance;
  }

  private buildQueryParams(request: ExtendedReportRequest): URLSearchParams {
    const params = new URLSearchParams();

    if (request.fromDate) {
      params.append('fromDate', request.fromDate);
    }
    if (request.toDate) {
      params.append('toDate', request.toDate);
    }
    if (request.period) {
      params.append('period', request.period);
    }
    if (request.groupBy) {
      params.append('groupBy', request.groupBy);
    }
    if (request.userIds && request.userIds.length > 0) {
      request.userIds.forEach(id => params.append('userIds', id));
    }

    return params;
  }

  async getDashboardSummary(request: ExtendedReportRequest): Promise<DashboardSummary> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<DashboardSummary>(
        `${this.baseUrl}/dashboard-summary?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching dashboard summary:', error);
      throw error;
    }
  }

  async getJobsCreatedReport(request: ExtendedReportRequest): Promise<JobsCreatedReport> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<JobsCreatedReport>(
        `${this.baseUrl}/jobs-created?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching jobs created report:', error);
      throw error;
    }
  }

  async getLostJobsReport(request: ExtendedReportRequest): Promise<LostJobsReport> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<LostJobsReport>(
        `${this.baseUrl}/lost-jobs?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching lost jobs report:', error);
      throw error;
    }
  }

  async getConversionFunnel(request: ExtendedReportRequest): Promise<ConversionFunnel> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<ConversionFunnel>(
        `${this.baseUrl}/conversion-funnel?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching conversion funnel:', error);
      throw error;
    }
  }

  async getUserComparison(request: ExtendedReportRequest): Promise<UserComparisonReport> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<UserComparisonReport>(
        `${this.baseUrl}/user-comparison?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching user comparison:', error);
      throw error;
    }
  }

  async getLeadSources(request: ExtendedReportRequest): Promise<LeadSourceReport[]> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<LeadSourceReport[]>(
        `${this.baseUrl}/lead-sources?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching lead sources:', error);
      throw error;
    }
  }

  async getRevenueByMonth(request: ExtendedReportRequest): Promise<RevenueByMonthReport> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<RevenueByMonthReport>(
        `${this.baseUrl}/revenue-by-month?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching revenue by month:', error);
      throw error;
    }
  }

  async getRevenueByUser(request: ExtendedReportRequest): Promise<RevenueByUserReport> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<RevenueByUserReport>(
        `${this.baseUrl}/revenue-by-user?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching revenue by user:', error);
      throw error;
    }
  }

  async getCycleTime(request: ExtendedReportRequest): Promise<CycleTimeReport> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<CycleTimeReport>(
        `${this.baseUrl}/cycle-time?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching cycle time:', error);
      throw error;
    }
  }

  async getPresentationOutcomes(request: ExtendedReportRequest): Promise<PresentationOutcomeReport> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<PresentationOutcomeReport>(
        `${this.baseUrl}/presentation-outcomes?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching presentation outcomes:', error);
      throw error;
    }
  }

  async getAppointmentCompletion(request: ExtendedReportRequest): Promise<AppointmentCompletionReport> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<AppointmentCompletionReport>(
        `${this.baseUrl}/appointment-completion?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching appointment completion:', error);
      throw error;
    }
  }

  async getSpecBreakdown(request: ExtendedReportRequest): Promise<SpecBreakdownReport> {
    try {
      const params = this.buildQueryParams(request);
      const response = await apiClient.get<SpecBreakdownReport>(
        `${this.baseUrl}/spec-breakdown?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching spec breakdown:', error);
      throw error;
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  formatPercentage(value: number): string {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-NZ').format(value);
  }
}

export const reportingService = ReportingService.getInstance();
